import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FlagIcon,
  PlusIcon
} from '@heroicons/react/24/outline'

import type { CoachConversation, GoalScope, UserGoal } from '../../../../../../../pure-js/types/CoachTypes'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { Button } from '../../../../../interaxDesignSystemComponents'
import { coachCardSx, coachCardWithPaddingSx } from '../../../../../shared/coachPreferences/coachSurfaceStyles'
import {
  COACH_SIDE_PANEL_RAIL_WIDTH_PX,
  COACH_SIDE_PANEL_WIDTH_PX,
  coachSidePanelContentSx,
  coachSidePanelFoldSx
} from '../coachPageStyles'
import { CoachConversationList } from './CoachConversationList'
import { CoachGoalsPanel } from './CoachGoalsPanel'

/** What the fold toggle points `aria-controls` at, so the disclosure names what it opens. */
const COACH_SIDE_PANEL_CONTENT_ID = 'coach-side-panel-content'

type CoachSidePanelProps = {
  goals: UserGoal[]
  conversations: CoachConversation[]
  overallSummary?: string
  selectedId?: string
  /** Turn count of the conversation being spoken right now; the stored count lags by one flush. */
  liveMessageCount?: number
  /** Before any goals exist, the open conversation has a job to finish first. */
  isFirstGoalSetting: boolean
  /** The conversation panel is showing its own start control, so this one must not offer a second. */
  isPanelOfferingStart: boolean
  /** Status and conversations are still arriving, so nothing here is known yet. */
  isLoading: boolean
  /** The goal list of the coaching focus in use; everyone but a leader only ever has the personal one. */
  goalScope: GoalScope
  onSelect: (id: string) => void
  onNew: () => void
}

/**
 * Everything beside the conversation, in one card: goals first, then the history — what someone is
 * working on should not sit below a list of chats. Folds to an icon rail to give the width back.
 */
export const CoachSidePanel = (props: CoachSidePanelProps) => {
  const { goals, conversations, overallSummary, selectedId, liveMessageCount } = props
  const { isFirstGoalSetting, isPanelOfferingStart, isLoading, goalScope, onSelect, onNew } = props
  const { t } = useTranslation()
  const theme = useTheme()

  // Only desktop has a column to give back — below `md` folding would just hide the goals.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [isFolded, setIsFolded] = useState(false)
  const isCollapsed = isDesktop && isFolded

  const collapse = useCallback(() => setIsFolded(true), [])
  const expand = useCallback(() => setIsFolded(false), [])

  // Folding unmounts the focused toggle and drops focus to the document, so it moves to whichever
  // control replaced it — but only when the toggle had focus, or it would steal it from the composer.
  const railButtonRef = useRef<HTMLButtonElement>(null)
  const collapseButtonRef = useRef<HTMLButtonElement>(null)
  const wasToggleFocusedRef = useRef(false)
  const rememberToggleFocus = useCallback(() => {
    const active = document.activeElement
    wasToggleFocusedRef.current = active === railButtonRef.current || active === collapseButtonRef.current
  }, [])
  useEffect(() => {
    if (!wasToggleFocusedRef.current) return
    wasToggleFocusedRef.current = false
    const counterpart = isCollapsed ? railButtonRef.current : collapseButtonRef.current
    counterpart?.focus()
  }, [isCollapsed])

  // Never two ways to begin at once: the conversation panel's own start wins, being the one in front of
  // the person. This button is for leaving a conversation you are already in.
  const canStartNewConversation = conversations.length > 0 && !isPanelOfferingStart

  // Held shut while the answer is unknown — the second or two the queries take is long enough to click.
  const blockNewConversation = isLoading || isFirstGoalSetting

  // The wrapper is what animates: the page grid sizes this column to its child, so transitioning the
  // width here is what makes the conversation grow into the space rather than jump into it.
  const foldWrapperSx = {
    ...coachSidePanelFoldSx,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: { xs: 'auto', sm: 'auto', md: 0 },
    width: {
      xs: '100%',
      sm: '100%',
      md: isCollapsed ? `${COACH_SIDE_PANEL_RAIL_WIDTH_PX}px` : `${COACH_SIDE_PANEL_WIDTH_PX}px`
    }
  }

  if (isCollapsed) {
    // One control, not three: three buttons doing the same thing read as three destinations to a screen
    // reader, and cost three tab stops for one action. The icons inside say what is behind it.
    return (
      <Box sx={foldWrapperSx}>
        <Box
          component="button"
          type="button"
          onClick={() => {
            rememberToggleFocus()
            expand()
          }}
          ref={railButtonRef}
          aria-expanded={false}
          aria-controls={COACH_SIDE_PANEL_CONTENT_ID}
          aria-label={t('gamer.ariaCoach.sidePanelExpand')}
          title={t('gamer.ariaCoach.sidePanelExpand')}
          data-testid={TESTIDS.COACH_SIDE_PANEL_RAIL}
          sx={{
            ...coachCardSx,
            ...coachSidePanelContentSx,
            width: '100%',
            py: 2,
            px: 0.5,
            height: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            color: INTERAX_COLORS.thunderGrey,
            '&:hover': { color: INTERAX_COLORS.white }
          }}
        >
          <ChevronLeftIcon width={20} />
          <FlagIcon width={20} color={INTERAX_COLORS.sun} />
          <ChatBubbleLeftRightIcon width={20} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={foldWrapperSx}>
      <Stack
        spacing={2.5}
        id={COACH_SIDE_PANEL_CONTENT_ID}
        data-testid={TESTIDS.COACH_SIDE_PANEL}
        sx={{
          ...coachCardWithPaddingSx,
          ...coachSidePanelContentSx,
          width: '100%',
          minWidth: 0,
          // Only desktop gives this column a bounded height to scroll inside; stacked, it grows with the page.
          minHeight: { xs: 'auto', sm: 'auto', md: 0 },
          overflowY: { xs: 'visible', sm: 'visible', md: 'auto' }
        }}
      >
        {canStartNewConversation && (
          <Stack spacing={1}>
            <Button
              variant="primary"
              onClick={onNew}
              disabled={blockNewConversation}
              icon={<PlusIcon width={18} />}
              iconPosition="left"
            >
              {t('gamer.ariaCoach.newConversation')}
            </Button>
            {blockNewConversation && !isLoading && (
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 12 }}>
                {t('gamer.ariaCoach.goalSetting.finishFirst')}
              </Typography>
            )}
          </Stack>
        )}

        <CoachGoalsPanel
          goals={goals}
          isLoading={isLoading}
          goalScope={goalScope}
          headerAction={
            isDesktop ? (
              <Tooltip title={t('gamer.ariaCoach.sidePanelCollapse')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    rememberToggleFocus()
                    collapse()
                  }}
                  ref={collapseButtonRef}
                  aria-expanded
                  aria-controls={COACH_SIDE_PANEL_CONTENT_ID}
                  aria-label={t('gamer.ariaCoach.sidePanelCollapse')}
                  data-testid={TESTIDS.COACH_SIDE_PANEL_TOGGLE}
                  sx={{ color: INTERAX_COLORS.thunderGrey, flexShrink: 0 }}
                >
                  <ChevronRightIcon width={18} />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        />

        <Divider sx={{ borderColor: INTERAX_COLORS.white10 }} />

        <CoachConversationList
          conversations={conversations}
          overallSummary={overallSummary}
          selectedId={selectedId}
          liveMessageCount={liveMessageCount}
          isLoading={isLoading}
          onSelect={onSelect}
        />
      </Stack>
    </Box>
  )
}
